import React, { useRef } from 'react';
import { OrderDetailDto } from '../types';
import { formatCurrency, formatDate, formatSkuDisplay } from '../utils';
import { useModalA11y } from '../hooks/use-modal-a11y';

interface PickListModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderDetail: OrderDetailDto;
}

export function PickListModal({ isOpen, onClose, orderDetail }: PickListModalProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, onClose);

  if (!isOpen) return null;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = printRef.current?.innerHTML || '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Pick List - Order ${orderDetail.orderNumber}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              padding: 20px;
              font-size: 12px;
            }
            .header { 
              border-bottom: 2px solid #000; 
              padding-bottom: 15px; 
              margin-bottom: 20px;
            }
            .header h1 { 
              font-size: 24px; 
              margin-bottom: 5px;
            }
            .header-info { 
              display: flex; 
              justify-content: space-between; 
              margin-top: 10px;
            }
            .info-section { 
              margin-bottom: 20px;
            }
            .info-section h2 { 
              font-size: 14px; 
              margin-bottom: 10px;
              background-color: #f0f0f0;
              padding: 5px 10px;
            }
            .info-grid { 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 10px;
            }
            .info-item { 
              padding: 5px 0;
            }
            .info-label { 
              font-weight: bold; 
              color: #666;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 20px;
            }
            th { 
              background-color: #f0f0f0; 
              padding: 10px; 
              text-align: left; 
              border: 1px solid #ddd;
              font-weight: bold;
            }
            td { 
              padding: 10px; 
              border: 1px solid #ddd;
            }
            .qty-box {
              width: 50px;
              height: 30px;
              border: 2px solid #000;
              display: inline-block;
              margin-left: 10px;
            }
            .picked-checkbox {
              width: 20px;
              height: 20px;
              border: 2px solid #000;
              display: inline-block;
            }
            .footer {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
            }
            .signature-line {
              margin-top: 40px;
              border-bottom: 1px solid #000;
              width: 300px;
            }
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
              button { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    
    // Small delay to ensure content is loaded before printing
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handlePopupFullscreen = () => {
    const printWindow = window.open('', '_blank', 'fullscreen=yes,width=' + screen.width + ',height=' + screen.height);
    if (!printWindow) return;

    const printContent = printRef.current?.innerHTML || '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Pick List - Order ${orderDetail.orderNumber}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              padding: 20px;
              font-size: 12px;
            }
            .header { 
              border-bottom: 2px solid #000; 
              padding-bottom: 15px; 
              margin-bottom: 20px;
            }
            .header h1 { 
              font-size: 24px; 
              margin-bottom: 5px;
            }
            .header-info { 
              display: flex; 
              justify-content: space-between; 
              margin-top: 10px;
            }
            .info-section { 
              margin-bottom: 20px;
            }
            .info-section h2 { 
              font-size: 14px; 
              margin-bottom: 10px;
              background-color: #f0f0f0;
              padding: 5px 10px;
            }
            .info-grid { 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 10px;
            }
            .info-item { 
              padding: 5px 0;
            }
            .info-label { 
              font-weight: bold; 
              color: #666;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 20px;
            }
            th { 
              background-color: #f0f0f0; 
              padding: 10px; 
              text-align: left; 
              border: 1px solid #ddd;
              font-weight: bold;
            }
            td { 
              padding: 10px; 
              border: 1px solid #ddd;
            }
            .qty-box {
              width: 50px;
              height: 30px;
              border: 2px solid #000;
              display: inline-block;
              margin-left: 10px;
            }
            .picked-checkbox {
              width: 20px;
              height: 20px;
              border: 2px solid #000;
              display: inline-block;
            }
            .footer {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
            }
            .signature-line {
              margin-top: 40px;
              border-bottom: 1px solid #000;
              width: 300px;
            }
            .action-bar {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              background: #1f2937;
              padding: 10px 20px;
              display: flex;
              gap: 10px;
              justify-content: flex-end;
              z-index: 1000;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .content-wrapper {
              margin-top: 60px;
            }
            button {
              padding: 8px 16px;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 500;
            }
            .btn-print {
              background: #2563eb;
              color: white;
            }
            .btn-print:hover {
              background: #1d4ed8;
            }
            .btn-close {
              background: #6b7280;
              color: white;
            }
            .btn-close:hover {
              background: #4b5563;
            }
            @media print {
              .action-bar { display: none; }
              .content-wrapper { margin-top: 0; }
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="action-bar">
            <button class="btn-print" onclick="window.print()">
              <i class="fa-solid fa-print"></i> Print
            </button>
            <button class="btn-close" onclick="window.close()">Close</button>
          </div>
          <div class="content-wrapper">
            ${printContent}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    
    // Try to make it fullscreen
    try {
      if (printWindow.document.documentElement.requestFullscreen) {
        printWindow.document.documentElement.requestFullscreen();
      }
    } catch (e) {
      console.log('Fullscreen not supported');
    }
  };

  // Group line items by vendor if available
  const lineItemsByVendor = orderDetail.lineItems.reduce((acc, item) => {
    const vendor = item.vendorName || 'Unassigned';
    if (!acc[vendor]) {
      acc[vendor] = [];
    }
    acc[vendor].push(item);
    return acc;
  }, {} as Record<string, typeof orderDetail.lineItems>);

  return (
    <div ref={dialogRef} className="fixed inset-0 z-50 overflow-y-auto focus:outline-none" aria-labelledby="modal-title" role="dialog" aria-modal="true" tabIndex={-1}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block w-full max-w-5xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-secondary-800 rounded-lg shadow-xl">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-secondary-900 border-b border-gray-200 dark:border-secondary-700 flex justify-between items-center no-print">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white" id="modal-title">
              Pick List - Order {orderDetail.orderNumber}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={handlePopupFullscreen}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                title="Open in fullscreen popup window"
              >
                <i className="fa-solid fa-expand mr-2"></i>
                Popup Fullscreen
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <i className="fa-solid fa-print mr-2"></i>
                Print
              </button>
              <button
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-200 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded-md hover:bg-gray-50 dark:hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
            <div ref={printRef}>
              {/* Pick List Header */}
              <div className="header">
                <h1>PICK LIST</h1>
                <div className="header-info">
                  <div>
                    <div><strong>Order #:</strong> {orderDetail.orderNumber}</div>
                    <div><strong>Date:</strong> {formatDate(new Date().toISOString())}</div>
                  </div>
                  <div>
                    <div><strong>Submitter:</strong> {orderDetail.submitterName}</div>
                    <div><strong>Division:</strong> {orderDetail.divisionName}</div>
                  </div>
                </div>
              </div>

              {/* Ship To Address and Special Instructions - Side by Side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                {/* Ship To Address */}
                {orderDetail.shippingAddress && (
                  <div className="info-section" style={{ marginBottom: 0 }}>
                    <h2>Ship To Address</h2>
                    <div>
                      {orderDetail.shippingAddress.contactName && <div><strong>{orderDetail.shippingAddress.contactName}</strong></div>}
                      {orderDetail.shippingAddress.street1 && <div>{orderDetail.shippingAddress.street1}</div>}
                      {orderDetail.shippingAddress.street2 && <div>{orderDetail.shippingAddress.street2}</div>}
                      <div>
                        {orderDetail.shippingAddress.city && <span>{orderDetail.shippingAddress.city}, </span>}
                        {orderDetail.shippingAddress.state && <span>{orderDetail.shippingAddress.state} </span>}
                        {orderDetail.shippingAddress.zipCode && <span>{orderDetail.shippingAddress.zipCode}</span>}
                      </div>
                      {orderDetail.shippingAddress.country && <div>{orderDetail.shippingAddress.country}</div>}
                      {orderDetail.shippingAddress.phone && <div>Phone: {orderDetail.shippingAddress.phone}</div>}
                    </div>
                  </div>
                )}

                {/* Order Notes */}
                {orderDetail.notes && (
                  <div className="info-section" style={{ marginBottom: 0 }}>
                    <h2>Special Instructions</h2>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{orderDetail.notes}</div>
                  </div>
                )}
              </div>

              {/* Items to Pick - Grouped by Vendor */}
              <div className="info-section">
                <h2>Items to Pick</h2>
                {Object.entries(lineItemsByVendor).map(([vendor, items]) => (
                  <div key={vendor} style={{ marginBottom: '30px' }}>
                    <h3 style={{ 
                      fontSize: '13px', 
                      fontWeight: 'bold', 
                      marginBottom: '10px',
                      padding: '5px 10px',
                      backgroundColor: '#e5e7eb'
                    }}>
                      {vendor}
                    </h3>
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>Picked</th>
                          <th style={{ width: '50px' }}>Line</th>
                          <th>Item Description</th>
                          <th style={{ width: '140px' }}>SKU / Part Numbers</th>
                          <th style={{ width: '100px' }}>Qty Ordered</th>
                          <th style={{ width: '100px' }}>Qty Picked</th>
                          <th style={{ width: '80px' }}>UOM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => {
                          const isDeclined = item.approvalStatus?.toLowerCase() === 'declined' || 
                                           item.approvalStatus?.toLowerCase() === 'rejected';
                          return (
                            <tr key={item.id} style={isDeclined ? { textDecoration: 'line-through', color: '#999' } : {}}>
                              <td style={{ textAlign: 'center' }}>
                                {!isDeclined && <span className="picked-checkbox"></span>}
                              </td>
                              <td style={{ textAlign: 'center' }}>{index + 1}</td>
                              <td>
                                <strong>{item.name}</strong>
                                {item.description && (
                                  <div style={{ fontSize: '11px', color: isDeclined ? '#999' : '#666', marginTop: '3px' }}>
                                    {item.description}
                                  </div>
                                )}
                              </td>
                              <td style={{ fontFamily: 'monospace' }}>
                                {formatSkuDisplay(item.sku, item.vendor) || 'N/A'}
                                {item.vendorPartNumber && (
                                  <div style={{ fontSize: '10px', color: '#666', marginTop: '3px' }}>
                                    Vendor P/N: {item.vendorPartNumber}
                                  </div>
                                )}
                              </td>
                              <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                {item.approvedQuantity || item.quantity}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {isDeclined ? '0' : <span className="qty-box"></span>}
                              </td>
                              <td style={{ textAlign: 'center' }}>{item.unitOfMeasure || 'EA'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="info-section">
                <h2>Order Summary</h2>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Total Items:</span> {orderDetail.lineItems.filter(item => {
                      const status = item.approvalStatus?.toLowerCase();
                      return status !== 'declined' && status !== 'rejected';
                    }).length}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Total Quantity:</span> {orderDetail.lineItems
                      .filter(item => {
                        const status = item.approvalStatus?.toLowerCase();
                        return status !== 'declined' && status !== 'rejected';
                      })
                      .reduce((sum, item) => sum + (item.approvedQuantity || item.quantity), 0)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Order Total:</span> {formatCurrency(orderDetail.totalAmount, orderDetail.currency)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Status:</span> {orderDetail.status}
                  </div>
                </div>
              </div>

              {/* Footer with signature */}
              <div className="footer">
                <div style={{ marginBottom: '20px' }}>
                  <strong>Notes / Issues:</strong>
                  <div style={{ 
                    border: '1px solid #ddd', 
                    minHeight: '60px', 
                    padding: '10px', 
                    marginTop: '5px' 
                  }}></div>
                </div>
                <div>
                  <strong>Picked By:</strong>
                  <div className="signature-line"></div>
                  <div style={{ marginTop: '5px', fontSize: '11px', color: '#666' }}>
                    Signature and Date
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
